// Must run before the payments partner's checkout script boots: it calls
// Object.hasOwn, which some browsers lack (POCKETSFLOW-FRONTEND-9P).
import "./lib/objectHasOwn";
// Must run before anything reads storage: in-memory localStorage /
// sessionStorage where the browser has none (Apple Mail, POCKETSFLOW-FRONTEND-9K).
import "./lib/storageFallback";
// Must run before React renders — hardens removeChild/insertBefore against
// translation-extension DOM corruption (POC-481).
import "./services/domTranslationGuard";
import { languageReady } from "./services/i18n";
import "./services/dayjs-setup";

import { QueryClientProvider } from "@tanstack/react-query";
import React, { Fragment, Suspense } from "react";
import { createRoot } from "react-dom/client";
import { Toaster } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { createBrowserRouter } from "react-router";
import { RouterProvider } from "react-router/dom";

import { RouteErrorFallback } from "./components/ErrorFallback";
import { PageLoader } from "./components/page-loader";
import { isSalesSurface, isStatusPage } from "./lib/surface";
import { initTheme } from "./lib/theme";
import { captureTrackedLink } from "./lib/trackedLink";
import {
	bootsHidden,
	POPUP_OPENED_MESSAGE,
	PREWARM_OPEN_MESSAGE,
} from "./pages/Sales/Checkout/prewarmProtocol";
import { captureAttribution } from "./services/attribution";
import { filterExtensionErrors } from "./services/filterExtensionErrors";
import { capturePartnerReferralFromLocation } from "./services/partnerReferral";
import { queryClient } from "./services/queryClient";
import {
	initSentry,
	reportConsoleError,
	scheduleSalesSentry,
} from "./services/sentry";
import { resolveSurface } from "./services/surface";
import { initVercelAnalytics } from "./services/vercelAnalytics";
import "./styles/styles.css";
// Shared mobile scroll-reachability shells (100dvh, safe-area, fixed-bar
// spacers). Layout only - desktop unchanged. See styles/mobile-shell.scss.
import "./styles/mobile-shell.scss";
import {
	LAZY_CHUNK_RELOAD_KEY,
	lazyWithRecovery,
} from "./utils/lazyWithRecovery";

// Sync persist of `/referral?code=` before React or Auth0 mount (POC-851).
capturePartnerReferralFromLocation();
// First-touch source (utm_*, referrer), also before Auth0 can redirect: a
// visitor landing on the app was otherwise only recorded after signing in.
captureAttribution(resolveSurface(window.location.hostname));
// Starter `trackedLinks`: keep the short link the buyer came through (30 days).
captureTrackedLink();

// Dashboard light/dark theme — before React renders so nothing paints in the
// wrong palette. Buyer-facing surfaces are always light (see lib/theme.ts).
initTheme();

/**
 * Booted hidden on a seller's page ahead of the click (the popup script's
 * prewarm, see pages/Sales/Checkout/prewarmProtocol.ts): runs `then` once the
 * popup opens, so a visitor who never clicks Buy is never counted.
 */
const whenShown = (then: () => void): void => {
	if (!bootsHidden(window.location)) {
		then();
		return;
	}
	const onOpen = (event: MessageEvent) => {
		const type = (event.data as { type?: unknown } | null)?.type;
		if (type !== POPUP_OPENED_MESSAGE && type !== PREWARM_OPEN_MESSAGE) return;
		window.removeEventListener("message", onOpen);
		then();
	};
	window.addEventListener("message", onOpen);
};

// Vercel Web Analytics: ~1KB deferred, cookieless pageviews on every surface.
// URLs are scrubbed of secrets/IDs before sending (see vercelAnalytics.ts).
whenShown(initVercelAnalytics);

// POC-629: dashboard-only dev tooling.
if (!isSalesSurface()) {
	import("./services/react-scan");
}

const Status = lazyWithRecovery(() => import("./pages/Status/Status"));
const DashboardProviders = lazyWithRecovery(
	() => import("./components/DashboardProviders/DashboardProviders"),
);

// POC-642: load ONLY the shell for this surface. Both branches are still
// dynamic-import()s (Rollup emits both async chunks), but keeping sales and
// dashboard route trees in separate modules stops TipTap/xyflow/Auth0 from
// being static deps of a shared app.tsx parent. styles.css is always imported
// above — never skip it for sales.
// POC-653: guard `.default` so a stale/broken shell chunk reloads once
// instead of throwing TypeError on /checkout (and other sales routes).
const salesSurface = isSalesSurface();
const App = lazyWithRecovery(() =>
	salesSurface ? import("./app.sales") : import("./app.dashboard"),
);

/**
 * POC-858 fallback warm for deep-linked checkouts, in ms. Aligned with
 * `EMBED_READY_BUDGET_MS` (`pages/Sales/Checkout/checkoutEmbedMount.ts`):
 * once the checkout has had its full readiness budget, the payment form is
 * either ready (the typeable gate already loaded Clarity) or stalled — and a
 * stalled or abandoned checkout must still produce its pageview.
 */
const CHECKOUT_ANALYTICS_WARM_FALLBACK_MS = 8000;

// POC-657: Clarity after first paint on sales (idle / timeout). Dynamic
// import keeps the analytics tag out of the buyer entry graph (POC-629 /
// POC-642). Dashboard inits the same helper immediately.
if (salesSurface) {
	// POC-862: a stale/broken lazy chunk can resolve to undefined; destructuring
	// it threw "Cannot destructure property 'scheduleSalesAnalytics'". Guard the
	// module and swallow so analytics boot can never crash the app shell.
	// POC-858 follow-up: deep links land buyers straight on /checkout, where
	// the spinner-to-typeable budget is owned by the intent POST + payment
	// embed. Do NOT load the Clarity tag at boot there — the Checkout holds sales
	// events and warms/releases only after payment fields are typeable.
	const bootsOnCheckout =
		typeof window !== "undefined" &&
		window.location.pathname.startsWith("/checkout");
	if (bootsOnCheckout) {
		// The checkout's typeable gate owns the warm on the happy path
		// (POC-858). This fallback covers the buyer who never gets there:
		// without it a stalled embed or an abandoned form produced no
		// `$pageview` at all, so checkout traffic was invisible in analytics.
		// Held events stay held — this only loads the tag, well
		// after the intent POST has settled.
		const scheduleWarmFallback = () =>
			window.setTimeout(() => {
				void import("./services/analytics")
					.then((mod) => mod?.warmSalesAnalytics?.())
					.catch(() => {});
			}, CHECKOUT_ANALYTICS_WARM_FALLBACK_MS);
		// Loaded hidden ahead of the click: no analytics until the popup opens.
		whenShown(scheduleWarmFallback);
	} else {
		void import("./services/analytics")
			.then((mod) => mod?.scheduleSalesAnalytics?.())
			.catch(() => {});
	}
	scheduleSalesSentry();
} else {
	initSentry();
	void import("./services/analytics")
		.then((mod) => {
			void mod?.ensureAnalytics?.();
		})
		.catch(() => {});
	// Our own Meta Pixel: dashboard only, never storefronts / checkout.
	void Promise.all([import("./services/metaPixel"), import("./services/api")])
		.then(([pixel, api]) => {
			pixel?.loadMetaPixel?.();
			if (api?.default) pixel?.watchFirstProductPublish?.(api.default);
		})
		.catch(() => {});
}

filterExtensionErrors(reportConsoleError);

// One recovery reload per broken chunk. The guard is cleared by
// lazyWithRecovery once a lazy module resolves — NOT on window.load, which
// fired before the failing route chunk was requested and re-armed the reload
// every cycle (infinite reload loop on a dev module error, POC-817).
window.addEventListener("vite:preloadError", (event) => {
	// Always swallow — a second CSS/JS preload 404 after the one-shot
	// reload must not throw (Sentry: Unable to preload CSS / lazy default).
	event.preventDefault();
	if (sessionStorage.getItem(LAZY_CHUNK_RELOAD_KEY) === "1") return;
	sessionStorage.setItem(LAZY_CHUNK_RELOAD_KEY, "1");
	window.location.reload();
});

/**
 * The dashboard re-mounts when its language changes (the picker, or the
 * account's language arriving on a first visit). Labels kept in state or
 * memo, and components that miss the change event, would otherwise stay in
 * the old language. The URL and the query cache survive the re-mount.
 */
function RemountOnLanguage({ children }: { children: React.ReactNode }) {
	const { i18n } = useTranslation();
	return <Fragment key={i18n.language}>{children}</Fragment>;
}

const container = document.getElementById("root") as HTMLElement;
const root = createRoot(container);

const toaster = (
	<Toaster
		toastOptions={{
			style: {
				background: "var(--pf-surface)",
				color: "var(--pf-fg)",
				border: "1px solid var(--pf-border-strong)",
				borderRadius: "var(--pf-radius-lg)",
				boxShadow: "var(--pf-shadow-lg)",
				fontSize: "13.5px",
				padding: "10px 14px",
				maxWidth: "420px",
			},
			success: {
				iconTheme: {
					primary: "var(--pf-success)",
					secondary: "var(--pf-surface)",
				},
			},
			error: {
				iconTheme: {
					primary: "var(--pf-danger)",
					secondary: "var(--pf-surface)",
				},
			},
		}}
	/>
);

if (isStatusPage()) {
	root.render(
		<React.StrictMode>
			<Suspense fallback={null}>
				<Status />
			</Suspense>
		</React.StrictMode>,
	);
} else {
	const router = createBrowserRouter([
		{
			path: "/*",
			element: (
				<Suspense fallback={<PageLoader />}>
					<App />
				</Suspense>
			),
			errorElement: <RouteErrorFallback />,
		},
	]);

	const renderApp = () =>
		root.render(
			<React.StrictMode>
				{salesSurface ? (
					<QueryClientProvider client={queryClient}>
						<RouterProvider router={router} />
						{toaster}
					</QueryClientProvider>
				) : (
					<QueryClientProvider client={queryClient}>
						<Suspense fallback={null}>
							<DashboardProviders>
								<RemountOnLanguage>
									<RouterProvider router={router} />
								</RemountOnLanguage>
								{toaster}
							</DashboardProviders>
						</Suspense>
					</QueryClientProvider>
				)}
			</React.StrictMode>,
		);
	// Buyers render at once (English-only). The dashboard waits for the stored
	// language so its first render is already in it.
	if (salesSurface) renderApp();
	else void languageReady().then(renderApp);
}
